import {
  Accordion,
  Box,
  Button,
  Grid,
  PageFilter,
  Select,
  Typography,
} from '@oresundsbron/bridge-ui';
import {
  fromNullable,
  fromPredicate,
  chain,
  toUndefined,
  map,
  getOrElse,
} from 'fp-ts/lib/Option';
import { filter, map as mapA } from 'fp-ts/lib/Array';
import { pipe } from 'fp-ts/lib/function';
import { FC, useMemo, useState } from 'react';
import useFAQ from '../../hooks/useFAQ';
import { FAQGroup } from '@oresundsbron/api';
import { RichText } from '../../components/RichText';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { cx } from 'class-variance-authority';
import { DefaultLink } from '~/components/Links/DefaultLink';

const toGroup = (path: string, t: (k: string) => string) => (group: FAQGroup) =>
  pipe(
    group.questions,
    fromNullable,
    map(
      mapA((q) => (
        <Accordion.Item key={q.sys.id} value={q.sys.id} label={q.question}>
          <Box className="flex flex-col">
            <RichText content={q.shortAnswer} />

            <Button
              href={`${path}/${group.slug}/${q.slug}`}
              as={DefaultLink}
              intent="text"
              className="ml-auto"
            >
              {t('action.readMore')}
            </Button>
          </Box>
        </Accordion.Item>
      ))
    ),
    map((qs) => (
      <Box key={group.sys.id} className="col-span-full mt-4">
        <Typography as="h2" intent="headline" size="lg" className="mb-6">
          {group?.title}
        </Typography>
        <Accordion.Root
          type="single"
          collapsible
          className={cx('grid', 'grid-cols-1', 'sm:grid-cols-2', 'gap-4')}
        >
          {qs}
        </Accordion.Root>
      </Box>
    )),
    toUndefined
  );

export const FAQ: FC = () => {
  const data = useFAQ();
  const { asPath } = useRouter();
  const { t } = useTranslation(['common'], { nsMode: 'fallback' });
  const [filt, setFilt] = useState<string | undefined>();

  const groups = useMemo(
    () =>
      pipe(
        data,
        fromNullable,
        chain(fromPredicate((x) => x.length > 0)),
        map((arr) =>
          pipe(
            filt,
            fromNullable,
            map(() => arr),
            map(filter((x) => x.sys.id === filt)),
            getOrElse(() => arr)
          )
        ),

        map(mapA(toGroup(asPath, t))),
        map(filter(Boolean)),
        toUndefined
      ),
    [data, asPath, t, filt]
  );

  const filters = useMemo(
    () =>
      pipe(
        data,
        fromNullable,
        map(
          mapA((g) => (
            <PageFilter.Button
              key={g.sys.id}
              active={filt === g.sys.id}
              filterKey={g.sys.id}
              label={g.title}
              onFilterChange={() => setFilt(g.sys.id)}
            />
          ))
        ),
        map((f) => (
          <PageFilter.Root className="col-span-full hidden border-b py-4 sm:block">
            <PageFilter.Button
              filterKey="all"
              label={t('faq.showAll')}
              active={filt === undefined}
              onFilterChange={() => setFilt(undefined)}
            />
            {f}
          </PageFilter.Root>
        )),
        toUndefined
      ),
    [data, t, filt, setFilt]
  );

  const options = useMemo(
    () =>
      pipe(
        data,
        fromNullable,
        map(mapA((g) => ({ label: g.title, value: g.sys.id }))),
        map((arr) => [{ label: t('faq.showAll'), value: 'show-all' }, ...arr]),
        toUndefined
      ),
    [data, t]
  );

  return (
    <Grid>
      <Typography className="col-span-full mb-2 text-center">
        {t('faq.browse')}
      </Typography>
      {options ? (
        <Select
          name="faq-group"
          className="col-span-full sm:hidden"
          label={t('faq.filterTitle')}
          value={filt || 'show-all'}
          onChange={(e) =>
            setFilt(e.target.value === 'show-all' ? undefined : e.target.value)
          }
          options={options}
        />
      ) : null}
      {filters}
      {groups}
    </Grid>
  );
};
